import {
  SALES_APPROVED_QUOTE_STATUS,
  effectiveQuoteStatus,
  isPreSalesApprovalStatus,
} from 'src/shared/consts/quoteStatus'

/**
 * Proposal alternatives (quote_item.slot). Every alternative stays on the quote
 * after approval; only one line per slot counts towards the order:
 * the one the customer accepted (customer_selected_at), otherwise Klayder's
 * pick (is_recommended), otherwise the first line of the slot.
 * Lines without a slot always count, so quotes without alternatives are unaffected.
 */
export interface QuoteLineForAlternatives {
  id: number
  slot?: string | null
  is_recommended?: boolean | null
  customer_selected_at?: string | null
}

export const activeQuoteLineIds = (
  lines: readonly QuoteLineForAlternatives[] | null | undefined
): Set<number> => {
  const active = new Set<number>()
  const bySlot = new Map<string, QuoteLineForAlternatives[]>()

  ;(lines ?? []).forEach((line) => {
    const slot = line.slot?.trim()
    if (!slot) {
      active.add(line.id)
      return
    }
    const group = bySlot.get(slot)
    if (group) group.push(line)
    else bySlot.set(slot, [line])
  })

  bySlot.forEach((group) => {
    const sorted = [...group].sort((a, b) => a.id - b.id)
    const chosen =
      sorted.find((line) => line.customer_selected_at) ??
      sorted.find((line) => line.is_recommended) ??
      sorted[0]
    active.add(chosen.id)
  })

  return active
}

export const filterActiveQuoteLines = <T extends QuoteLineForAlternatives>(
  lines: readonly T[] | null | undefined
): T[] => {
  const active = activeQuoteLineIds(lines)
  return (lines ?? []).filter((line) => active.has(line.id))
}

/**
 * Before the customer approves: draft, in review, or ready to approve (`1_5`).
 * From approval on, the customer's choice and the order total are recorded.
 */
export const isBeforeCustomerApproval = (status: string | null | undefined) => {
  const effective = effectiveQuoteStatus(status)
  return (
    isPreSalesApprovalStatus(effective) ||
    effective === SALES_APPROVED_QUOTE_STATUS
  )
}

/**
 * The customer's accepted line: its product, colour, quantity, price and place
 * in the slot are what was approved, so staff edits to those must not change
 * it afterwards (notes, designs and proofs still can). Keyed on the recorded
 * choice, not the status, so moving the order back to Draft keeps it locked;
 * only a new customer approval rewrites customer_selected_at.
 */
export const isLockedApprovedChoice = (line: {
  customer_selected_at?: string | null
}) => !!line.customer_selected_at

/** quote_item fields that change what the customer approved. */
export const APPROVED_CHOICE_FIELDS = [
  'product_id',
  'product_variant_color_id',
  'quantity',
  'unit_price',
  'discount',
  'sizes',
  'type',
  'slot',
  'is_recommended',
  'customer_selected_at',
  'quote_id',
] as const

/**
 * Admin edits to alternatives. Each `plan*` returns only the rows whose slot or
 * recommendation changes, so the caller writes exactly those. A slot has at
 * most one recommended line ("Our pick"), only ever set by the admin: removing
 * or deleting the pick leaves the slot with none, and the customer's default
 * then falls back to the first line (activeQuoteLineIds). A slot left with a
 * single line is dissolved (that line goes back to an ordinary line).
 */
export interface AlternativeUpdate {
  id: number
  slot: string | null
  is_recommended: boolean
}

const slotOf = (line: QuoteLineForAlternatives) => line.slot?.trim() || null

/** The other lines sharing this line's slot (empty for an ordinary line). */
export const slotSiblings = <T extends QuoteLineForAlternatives>(
  lines: readonly T[],
  id: number
): T[] => {
  const slot = slotOf(lines.find((line) => line.id === id) ?? { id })
  if (!slot) return []
  return lines.filter((line) => line.id !== id && slotOf(line) === slot)
}

/** Slot titles already used by groups other than `exceptId`'s own. */
export const slotTitleTaken = (
  lines: readonly QuoteLineForAlternatives[],
  title: string,
  exceptId?: number
) => {
  const wanted = title.trim().toLowerCase()
  const own = exceptId
    ? slotOf(lines.find((line) => line.id === exceptId) ?? { id: exceptId })
    : null
  return lines.some((line) => {
    const slot = slotOf(line)
    return !!slot && slot !== own && slot.toLowerCase() === wanted
  })
}

const diff = (
  lines: readonly QuoteLineForAlternatives[],
  next: Map<number, { slot: string | null; is_recommended: boolean }>
): AlternativeUpdate[] =>
  lines.flatMap((line) => {
    const want = next.get(line.id)
    if (!want) return []
    const same =
      slotOf(line) === want.slot &&
      !!line.is_recommended === want.is_recommended
    return same ? [] : [{ id: line.id, ...want }]
  })

/**
 * Rebuilds one slot: dissolves it when one line is left, otherwise keeps at
 * most one recommended line (`preferredId`, else the existing pick, else none).
 */
const settleSlot = (
  members: QuoteLineForAlternatives[],
  slot: string,
  next: Map<number, { slot: string | null; is_recommended: boolean }>,
  preferredId?: number
) => {
  if (members.length === 1) {
    next.set(members[0].id, { slot: null, is_recommended: false })
    return
  }
  const sorted = [...members].sort((a, b) => a.id - b.id)
  const pick =
    sorted.find((line) => line.id === preferredId) ??
    sorted.find((line) => line.is_recommended)
  sorted.forEach((line) =>
    next.set(line.id, { slot, is_recommended: line.id === pick?.id })
  )
}

/**
 * After deleting a line: settles what is left of its slot (`remaining` are the
 * slot's other lines). The pick is not moved; a last line becomes ordinary.
 */
export const planAfterDelete = (
  remaining: readonly QuoteLineForAlternatives[]
): AlternativeUpdate[] => {
  const slot = remaining.length ? slotOf(remaining[0]) : null
  if (!slot) return []
  const next = new Map<
    number,
    { slot: string | null; is_recommended: boolean }
  >()
  settleSlot([...remaining], slot, next)
  return diff(remaining, next)
}

/**
 * Puts `sourceId` next to `targetId` as an alternative, in the target's slot
 * (renamed to `title`), or a new slot when the target has none. The target's
 * slot keeps its recommendation; a new slot recommends the target.
 */
export const planAddAlternative = (
  lines: readonly QuoteLineForAlternatives[],
  sourceId: number,
  targetId: number,
  title: string
): AlternativeUpdate[] => {
  const slot = title.trim()
  const source = lines.find((line) => line.id === sourceId)
  const target = lines.find((line) => line.id === targetId)
  if (!slot || !source || !target || sourceId === targetId) return []

  // Already alternatives of each other: only the title can change.
  if (slotOf(source) && slotOf(source) === slotOf(target)) {
    return planRenameSlot(lines, targetId, slot)
  }

  const next = new Map<
    number,
    { slot: string | null; is_recommended: boolean }
  >()
  const targetGroup = [target, ...slotSiblings(lines, targetId)].filter(
    (line) => line.id !== sourceId
  )

  // Leaving its old slot can dissolve or re-recommend that slot.
  const sourceSlot = slotOf(source)
  if (sourceSlot && sourceSlot !== slotOf(target)) {
    settleSlot(slotSiblings(lines, sourceId), sourceSlot, next)
  }

  const hasPick = targetGroup.some((line) => line.is_recommended)
  settleSlot(
    [...targetGroup, { ...source, is_recommended: false }],
    slot,
    next,
    hasPick ? undefined : targetId
  )
  return diff(lines, next)
}

/** Makes `id` the recommended line of its slot. */
export const planMarkRecommended = (
  lines: readonly QuoteLineForAlternatives[],
  id: number
): AlternativeUpdate[] => {
  const line = lines.find((item) => item.id === id)
  const slot = line ? slotOf(line) : null
  if (!line || !slot) return []
  const next = new Map<
    number,
    { slot: string | null; is_recommended: boolean }
  >()
  settleSlot([line, ...slotSiblings(lines, id)], slot, next, id)
  return diff(lines, next)
}

/** Takes `id` out of its slot; the rest of the slot is settled again. */
export const planRemoveAlternative = (
  lines: readonly QuoteLineForAlternatives[],
  id: number
): AlternativeUpdate[] => {
  const line = lines.find((item) => item.id === id)
  const slot = line ? slotOf(line) : null
  if (!line || !slot) return []
  const next = new Map<
    number,
    { slot: string | null; is_recommended: boolean }
  >()
  next.set(id, { slot: null, is_recommended: false })
  settleSlot(slotSiblings(lines, id), slot, next)
  return diff(lines, next)
}

/** Renames the slot `id` belongs to (the line title the customer sees). */
export const planRenameSlot = (
  lines: readonly QuoteLineForAlternatives[],
  id: number,
  title: string
): AlternativeUpdate[] => {
  const slot = title.trim()
  const line = lines.find((item) => item.id === id)
  if (!slot || !line || !slotOf(line)) return []
  const next = new Map<
    number,
    { slot: string | null; is_recommended: boolean }
  >()
  ;[line, ...slotSiblings(lines, id)].forEach((member) =>
    next.set(member.id, { slot, is_recommended: !!member.is_recommended })
  )
  return diff(lines, next)
}
